<script lang="ts">
	/**
	 * An `<a>` that knows about the router.
	 *
	 * Plain anchors do a full page load, which throws away the SPA state and
	 * re-runs the AbstractID handshake. This wires `use:link` so in-app paths are
	 * pushed onto the history stack instead, and `use:active` so the link marks
	 * itself when it points at the current route — that's what makes nav items
	 * light up without every caller re-deriving `$location`.
	 *
	 * External destinations can't go through `use:link` (the action throws on
	 * anything that doesn't start with `/`), so they take a plain-anchor branch
	 * and get `target="_blank"`, `rel="noopener noreferrer"` and the
	 * `ExternalLinkIcon` — the visual promise that the click leaves the app.
	 *
	 * ```svelte
	 * <RoutedLink to="/Settings/Admin/Customers" class="text-primary-600 hover:underline">Customers</RoutedLink>
	 * <RoutedLink to="https://abstract3d.com">Abstract 3D</RoutedLink>
	 * ```
	 */

	import { link } from '../svelte-spa-router';
	import spaActive from '../svelte-spa-router/active.js';
	import ExternalLinkIcon from './ExternalLinkIcon.svelte';

	/** Destination. A path (`/Settings/Admin/Customers`) routes in-app; anything else is treated as external. */
	export let to: string = '/';
	/** Class added while `to` matches the current route. `active` is styled globally for `ul.appNav`. */
	export let activeClass: string = 'active';
	/**
	 * Route pattern that counts as "active", when it differs from `to`. A section
	 * link needs `activePath="/Settings/*"` so it stays lit on every sub-page,
	 * while still navigating to `/Settings` itself.
	 */
	export let activePath: string | undefined = undefined;
	/** Class added while it does not match. Empty adds nothing. */
	export let inactiveClass: string = '';
	/** Forces the external treatment for a destination that would otherwise route in-app. */
	export let external: boolean = false;
	/** Overrides the automatic external glyph — set `false` on icon-only or button-like links. */
	export let showExternalIcon: boolean | undefined = undefined;
	/** Anchor target. Defaults to `_blank` for external destinations, unset for internal ones. */
	export let target: string | undefined = undefined;

	let className: string = '';
	export { className as class };

	// A destination is "internal" only when the router can actually handle it —
	// `use:link` throws for hrefs not starting with '/', so mailto:, tel:, https:
	// and protocol-relative URLs all have to bypass it.
	$: isExternal = external || !to.startsWith('/') || to.startsWith('//');
	$: resolvedTarget = target ?? (isExternal ? '_blank' : undefined);
	// Only `_blank` needs the reverse-tabnabbing guard; leaving `rel` unset otherwise
	// keeps the rendered markup clean.
	$: resolvedRel = resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined;
	$: withIcon = showExternalIcon ?? isExternal;
</script>

{#if isExternal}
	<a {...$$restProps} href={to} target={resolvedTarget} rel={resolvedRel} class={className} on:click>
		<slot />{#if withIcon}<ExternalLinkIcon class="ml-1 inline h-3 w-3 align-baseline" />{/if}
	</a>
{:else}
	<a
		{...$$restProps}
		use:link
		use:spaActive={{ path: activePath ?? to, className: activeClass, inactiveClassName: inactiveClass }}
		href={to}
		target={resolvedTarget}
		rel={resolvedRel}
		class={className}
		on:click>
		<slot />{#if withIcon}<ExternalLinkIcon class="ml-1 inline h-3 w-3 align-baseline" />{/if}
	</a>
{/if}
